import React from 'react';
import { useRecoilState } from 'recoil';
import { langAtom, changeLanguage } from '../Atom/langAtom';
import itFlag from '../Assets/it.svg';
import enFlag from '../Assets/gb.svg';
import ptFlag from '../Assets/pt.svg';

const FLAGS = { it: itFlag, en: enFlag, pt: ptFlag };

export default function LanguageSelector({ className = '' }) {
  // If the frontend is built/run in production we force-only Italian and hide the selector.
  // React build sets process.env.NODE_ENV at build time. We also support an opt-in env
  // variable REACT_APP_ONLY_ITALIAN which, if set to 'true', will hide the selector as well.
  const isProd = (process.env.NODE_ENV === 'production') || (process.env.REACT_APP_ONLY_ITALIAN === 'true');

  // Hooks must be called unconditionally. We compute isProd first, then call hooks,
  // and early-return the render output below when in production.
  const [language] = useRecoilState(langAtom);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();
  const options = [
    { key: 'it', label: 'Italiano' },
    { key: 'en', label: 'English' },
    { key: 'pt', label: 'Português' },
  ];
  React.useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Do not render selector in production (hooks already called above)
  if (isProd) return null;

  const handleSelect = (key) => {
    changeLanguage(key);
    setOpen(false);
  };

  return (
    <div className={`relative inline-block text-left ${className}`} ref={ref}>
      <button
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-gray-200 shadow-sm"
      >
        <img src={FLAGS[language]} alt={language} className="w-5 h-4 object-cover rounded-sm" />
        <span className="hidden sm:inline-block">{options.find(o => o.key === language)?.label}</span>
        <svg className="w-3 h-3 ml-1 text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-40">
          <ul role="listbox" aria-label="Select language">
            {options.map(o => (
              <li key={o.key}>
                <button
                  onClick={() => handleSelect(o.key)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50 ${o.key === language ? 'bg-gray-100' : ''}`}
                >
                  <img src={FLAGS[o.key]} alt={o.key} className="w-5 h-4 object-cover rounded-sm" />
                  <span className="truncate">{o.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
